import { UploadOutlined } from "@ant-design/icons";
import { Button, Spin, Upload, message } from "antd";
import { UploadChangeParam } from "antd/lib/upload";
import { RcFile, UploadFile } from "antd/lib/upload/interface";
import { UploadRequestOption as RcCustomRequestOptions } from "rc-upload/lib/interface";
import React from "react";
import { FileTypes } from "../helpers/shared-interfaces";
import { decodeFileName, extractFileName, request } from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { generateDisplayErrorMessage } from "../helpers/utilities";


const UPLOAD_FILE = `
  mutation UploadFile($data: String!, $type: String, $opportunityId: ID, $extension: String!, $skipJobProcess: Boolean, $source: String, $name: String, $companyId: ID, $customerId: ID){
    uploadFile(data: $data, type: $type, opportunityId: $opportunityId, extension: $extension, skipJobProcess: $skipJobProcess, source: $source, name: $name, companyId: $companyId, customerId: $customerId){
      url
      name
      errors {
        property
        description
      }
    }
  }
`;

const fileSizeLimit = 10000000;

export const UploadPreview = (props: UploadFile): JSX.Element => {
  return (
    <a target="_blank" href={props.url} rel="noopener noreferrer">
      <div
        style={{
          backgroundColor: "rgb(29, 55, 100)",
          borderRadius: "4px",
          marginTop: "10px",
          boxSizing: "border-box",
          padding: "12px",
        }}
        key={props.name}
      >
        <h4 style={{ color: "white" }}>{props.name}</h4>
        <span style={{ position: "relative", top: "-5px" }}>View File</span>
      </div>
    </a>
  );
};

export interface UploadedDocument {
  url: string;
  name: string;
}

interface Props {
  onChange?: (combinedInfo: string) => void;
  /**
   * Fires with every file uploaded through this control (and again whenever one
   * is removed), so callers that accept more than one document can keep the
   * whole set. `onChange` still fires per-upload with only the latest file.
   */
  onFilesChange?: (files: UploadedDocument[]) => void;
  /** Allows selecting more than one file at a time in the picker. */
  multiple?: boolean;
  defaultFileList?: UploadFile[];
  style?: React.CSSProperties;
  elementStyle?: React.CSSProperties;
  opportunityId?: string | null;
  companyId?: string | null;
  type?: string;
  isTriggeredManually?: boolean;
  toggleModal?: () => void;
  skipJobProcess?: boolean;
  customerId?: string | null;
  onFileDelete?: (file: UploadFile) => void;
  accept?: string; // e.g., ".pdf,.doc,.docx" or "image/*" or ".pdf,application/pdf"
}

interface State {
  isLoading: boolean;
  fileUrl: string;
  fileList: UploadFile[];
  myFileList: UploadFile[];
  file: UploadFile | RcFile | undefined;
  uploadedDocuments: UploadedDocument[];
}

export default class FileUpload extends React.Component<Props, State> {
  state = {
    isLoading: false,
    fileUrl: "",
    fileList: [],
    myFileList: [],
    file: undefined,
    uploadedDocuments: [] as UploadedDocument[],
  };

  componentDidMount() {
    if (this.props.defaultFileList) {
      const decodedFileList = this.props.defaultFileList.map((file) => ({
        ...file,
        name: decodeFileName(file.name || file.url || ""),
      }));
      this.setState({ fileList: decodedFileList });
    }
  }

  toBase64 = (file: File | Blob | UploadFile | undefined): Promise<unknown> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file as Blob);
      reader.onload = (): void => resolve(reader.result);
    });

  uploadFileAutomatically = async (
    options: RcCustomRequestOptions,
  ): Promise<void> => {
    const file: RcFile = options.file as unknown as RcFile;

    await this.uploadFile(file);
  };

  uploadFileManually = async (): Promise<void> => {
    const { fileList } = this.state;

    const file: UploadFile = fileList.pop() as unknown as UploadFile;

    await this.uploadFile(file);
  };

  uploadFile = async (file: UploadFile | RcFile): Promise<void> => {
    const [name, extension] = file.name.split(".");

    if (file.name.split(".").length > 2) {
      message.error("Filename must not contain a dot(.) Kindly rename");
      return;
    }

    if (file.size && file.size > fileSizeLimit) {
      message.error("Maximum file size limit (10MB) exceeded.");
      return;
    }

    if (
      (this.props.type === FileTypes.employeeDatasheet ||
        this.props.type === FileTypes.users) &&
      !["xlsx", "csv"].includes(extension as string)
    ) {
      message.error("Only EXCEL and CSV files sheets can be uploaded");
      return;
    }

    this.setState({ isLoading: true });

    try {
      const base64Image = await this.toBase64(file);

      const { data }: any = await request({
        query: UPLOAD_FILE,
        variables: {
          data: `${base64Image}`,
          type: this.props.type,
          opportunityId: this.props.opportunityId,
          companyId: this.props.companyId,
          customerId: this.props.customerId,
          skipJobProcess: this.props.skipJobProcess,
          extension,
          source: "phoenix",
          name,
        },
      });

      const url = data.data?.uploadFile?.url;
      const filename = data.data?.uploadFile?.name;
      const displayName = extractFileName(filename || file.name); // Extract just the name part
      const combinedInfo = `${url}@@@${filename}`;

      message.success("File uploaded successfully");
      this.props.toggleModal && this.props.toggleModal();
      this.props.onChange && this.props.onChange(combinedInfo);

      if (url) {
        const uploadedDocuments = [
          ...this.state.uploadedDocuments.filter((doc) => doc.url !== url),
          { url, name: displayName },
        ];
        this.setState({ uploadedDocuments });
        this.props.onFilesChange && this.props.onFilesChange(uploadedDocuments);
      }

      this.setState({
        fileUrl: url,
        fileList: [
          ...this.state.fileList,
          {
            name: displayName,
            size: 1000,
            uid: data.data?.uploadFile?.url,
            type: file.type,
            url,
          },
        ],
        file: {
          name: displayName,
          size: 1000,
          uid: data.data?.uploadFile?.url,
          type: file.type,
          url: data.data?.uploadFile?.url,
        },
      });
    } catch (error) {
      message.error(`Failed to upload file - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleChange = (
    info: UploadChangeParam<UploadFile<Record<string, unknown>>>,
  ): void => {
    if (info.file.name.split(".").length > 2) {
      this.handleRemove(info.file);
    }
    if (info.file.status === "done") {
      message.success(`${info.file.name} file upload successfully.`);
      this.props.toggleModal && this.props.toggleModal();
    }
    if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
    if (info.file.status === "removed") {
      message.success(`${info.file.name} file removed successfully.`);
    }
  };

  beforeUpload = (file: UploadFile): boolean | Promise<File> | string => {
    const { isTriggeredManually, accept } = this.props

    if (file.name.split(".").length > 2) {
      message.error(`${file.name} file upload failed.`, 5);
      message.error("Filename must not contain a dot(.) Kindly rename", 5);
      return Upload.LIST_IGNORE;
    }

    // Validate file type against accept prop
    if (accept) {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      const fileType = file.type?.toLowerCase() || '';
      
      const acceptedTypes = accept.toLowerCase().split(',').map(type => type.trim());
      
      let isValidType = false;
      
      for (const acceptType of acceptedTypes) {
        if (acceptType.startsWith('.')) {
          // File extension check (e.g., .pdf, .doc)
          if (fileExtension === acceptType) {
            isValidType = true;
            break;
          }
        } else if (acceptType.includes('/*')) {
          // Wildcard MIME type check (e.g., image/*)
          const typePrefix = acceptType.split('/')[0];
          if (fileType.startsWith(typePrefix + '/')) {
            isValidType = true;
            break;
          }
        } else {
          // Exact MIME type check (e.g., application/pdf)
          if (fileType === acceptType) {
            isValidType = true;
            break;
          }
        }
      }
      
      if (!isValidType) {
        message.error(`Invalid file type. Please upload files matching: ${accept}`, 5);
        return Upload.LIST_IGNORE;
      }
    }

    if (isTriggeredManually) {
      this.setState({
        fileList: [...this.state.fileList, file],
      });
      return false;
    }

    return true;
  };

  handleRemove = (file: UploadFile): boolean => {
    // Uploaded files carry the remote url as their uid, so match on either.
    const removedUrl = file.url || (file.uid as string);
    const uploadedDocuments = this.state.uploadedDocuments.filter(
      (doc) => doc.url !== removedUrl,
    );

    this.setState((prevState) => ({
      fileList: prevState.fileList.filter((item) => item.uid !== file.uid),
      uploadedDocuments,
    }));

    this.props.onFilesChange && this.props.onFilesChange(uploadedDocuments);
    this.props.onFileDelete && this.props.onFileDelete(file);

    return true;
  };

  render(): JSX.Element {
    const { isLoading, fileList } = this.state;
    const triggerManually = this.props.isTriggeredManually
      ? this.props.isTriggeredManually
      : false;

    const displayFileList =
      fileList.length > 0
        ? fileList
        : this.props.defaultFileList?.map((file) => ({
            ...file,
            name: decodeFileName(file.name || file.url || ""),
          }));

    return (
      <>
        {triggerManually && (
          <div>
            <Upload
              name="avatar"
              className="avatar-uploader"
              listType="picture"
              defaultFileList={displayFileList}
              beforeUpload={this.beforeUpload}
              onChange={this.handleChange}
              style={this.props.style}
              fileList={fileList.length > 0 ? fileList : undefined}
              showUploadList={true}
              multiple={this.props.multiple}
              onRemove={(file) => this.handleRemove(file)}
              accept={this.props.accept}
            >
              <Spin spinning={isLoading}>
                <Button style={this.props.elementStyle}>
                  <UploadOutlined /> Click to Upload (
                  <b className="warning-btn">
                    Maximum size: {fileSizeLimit / 1000000}MB
                  </b>
                  )
                </Button>
              </Spin>
            </Upload>

            <Button
              danger
              type="primary"
              onClick={this.props.toggleModal}
              style={{ marginTop: 70, marginLeft: 260, marginRight: 10 }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              onClick={this.uploadFileManually}
              disabled={this.state.fileList.length === 0}
              loading={isLoading}
              style={{ marginTop: 70 }}
            >
              {isLoading ? "Uploading" : "Upload File"}
            </Button>
          </div>
        )}
        {!triggerManually && (
          <Upload
            name="avatar"
            className="avatar-uploader"
            listType="picture"
            defaultFileList={this.props.defaultFileList}
            beforeUpload={this.beforeUpload}
            showUploadList={true}
            multiple={this.props.multiple}
            fileList={fileList.length > 0 ? fileList : undefined}
            onRemove={(file) => this.handleRemove(file)}
            customRequest={this.uploadFileAutomatically}
            onChange={this.handleChange}
            style={this.props.style}
            accept={this.props.accept}
          >
            <Spin spinning={isLoading}>
              <Button style={this.props.elementStyle}>
                <UploadOutlined /> Click to Upload (
                <b className="warning-btn">
                  Maximum size: {fileSizeLimit / 1000000}MB
                </b>
                )
              </Button>
            </Spin>
          </Upload>
        )}
      </>
    );
  }
}
